import { TAB_STORAGE_KEY } from '@core/layout/ribbon/ribbonMoves'
import TourHost from '@core/layout/tour/TourHost'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import {
  ACTIONS,
  captureNextChord,
  exportBindings,
  getChord,
  importBindings,
  resetAllBindings,
  resetChord,
  setChord,
  subscribeBindings,
  useEscape,
  useShortcutHandler,
} from '@core/shortcuts'
import {
  Globe2,
  Braces,
  ChevronUp,
  ChevronDown,
  FolderOpen,
  Plus,
  Building2,
  GitCompareArrows,
  ClipboardCheck,
  SquaresIntersect,
  Database,
  Focus,
  Box,
  MousePointerSquareDashed,
  Eraser,
  Filter,
  EyeOff,
  Eye,
  Target,
  ArrowLeftRight,
  Grid3x3,
  Palette,
  Sun,
  Moon,
  Spline,
  Sparkles,
  Box as BoxIcon,
  Keyboard,
  Camera,
  Ruler,
  Square,
  Crosshair,
  Layers,
  LocateFixed,
  Map as MapIcon,
  Hand,
  Trash2,
  Type,
  ArrowUpRight,
  Cloud,
  Ghost,
  Link as LinkIcon,
  DraftingCompass,
  Download,
  FileText,
  GitBranch,
  Server,
  ScanLine,
  Network,
  MapPin,
  Merge,
  FileSignature,
  ScanEye,
  TableProperties,
  Presentation,
  type LucideIcon,
} from 'lucide-react'
import * as THREE from 'three'
import { useAppStore } from '@core/store'
import RibbonMenu from './RibbonMenu'
import { isFeatureEnabled } from '@core/features'
import { RAIL_ITEMS, type RailItemId } from '@core/layout/rail/railItems'
import { ICONES_DA_BARRA, alternarPainel, painelAberto } from '@core/layout/rail/painelDaBarra'
import {
  GridsStoreyFilterButton,
  OriginKindsFilterButton,
} from './filterButtons'
import { loadCollapsed, persistCollapsed } from './useRibbonShortcuts'
import { viewerApi } from '@viewer/lib/viewerApi'
import { DEFAULT_SAMPLE, SAMPLE_IFCS } from '@viewer/lib/loadSampleIfc'
import { useSampleLoad } from '@viewer/lib/useSampleLoad'
import { sampleShareUrl } from '@viewer/lib/deepLink'
import { measureApi } from '@viewer/measure/measureApi'
import type {
  MeasureMode,
  LengthUnit,
  WorkPlaneKind,
} from '@viewer/store/measureSlice'
import { downloadMcpSnapshot } from '@modules/assistant/lib/mcpSnapshot'

function modelsBbox(): THREE.Box3 {
  const box = new THREE.Box3()
  let initialised = false
  for (const [, model] of viewerApi.iterModels()) {
    const mb = model.box
    if (!mb || mb.isEmpty()) continue
    if (!initialised) {
      box.copy(mb)
      initialised = true
    } else {
      box.union(mb)
    }
  }
  return box
}

function computeModelsBboxCenter(): [number, number, number] {
  const box = modelsBbox()
  if (box.isEmpty()) return [0, 0, 0]
  const c = new THREE.Vector3()
  box.getCenter(c)
  return [c.x, c.y, c.z]
}

function computeModelsMinY(): number {
  const box = modelsBbox()
  if (box.isEmpty()) return 0
  return box.min.y
}
import {
  isFsAccessSupported,
  pickIfcFile,
  pickIfcFiles,
} from '@core/session/ifcHandles'
import { modelAcceptAttr } from '@core/session/modelFileTypes'

// ---------------------------------------------------------------------------
// Ribbon: Revit-style tabbed toolbar that lives below the global topbar.
//
// Layout:
//   ┌─────────────────────────────────────────────────────────────────┐
//   │  Ficheiro   Vista   Visibilidade                       ▴ collapse│ ← tab strip
//   ├─────────────────────────────────────────────────────────────────┤
//   │  [ button ]  [ button ]  │  [ button ]  │ ...                   │
//   │   Modelo                  Comparação                            │ ← panel titles
//   └─────────────────────────────────────────────────────────────────┘
//
// State: which tab is active and whether the ribbon is collapsed.
// Both persist in localStorage so the experience is sticky between
// sessions. Collapsed mode hides the panels row and keeps just the
// tab strip (~32px tall).
//
// Buttons are declarative below in tabsConfig; each entry resolves to
// a RibbonButton (large) or RibbonToggle (large pressed-state). The
// "Parser IFC paralelo" advanced toggle uses RibbonSmallToggle to live
// inside a vertically-stacked column on the right of its panel.
// ---------------------------------------------------------------------------

// A chave vive em ribbonMoves.ts, que tambem precisa dela. Estava
// escrita nos dois sitios e a copia de la ficou com outro nome.

type TabId =
  | 'ficheiro'
  | 'vista'
  | 'aparencia'
  | 'medicoes'
  | 'anotacoes'

interface RibbonProps {
  /** Drop the ribbon entirely (e.g. when the viewer is hidden behind a
   *  full-screen module pane). The host shell decides; we only render
   *  what we're asked to render. */
  visible?: boolean
}

function loadActiveTab(): TabId {
  try {
    const v = localStorage.getItem(TAB_STORAGE_KEY)
    if (
      v === 'ficheiro' ||
      v === 'vista' ||
      v === 'aparencia' ||
      v === 'medicoes' ||
      v === 'anotacoes'
    )
      return v
    // Separadores antigos que foram absorvidos.
    if (v === 'visibilidade') return 'vista'
    if (v === 'cena') return 'aparencia'
  } catch {
    /* ignore */
  }
  return 'ficheiro'
}

function persistTab(tab: TabId) {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, tab)
  } catch {
    /* ignore */
  }
}

function Ribbon({ visible = true }: RibbonProps) {
  const [activeTab, setActiveTab] = useState<TabId>(loadActiveTab)
  const [collapsed, setCollapsed] = useState<boolean>(loadCollapsed)

  // Listen for the Ctrl+F1 shortcut from useRibbonShortcuts. The shortcut
  // updates localStorage; we mirror to local state so the panels row
  // collapses/expands without remounting.
  useEffect(() => {
    const onToggle = () => setCollapsed(loadCollapsed())
    window.addEventListener('vn2r:ribbon-collapsed-toggle', onToggle)
    return () =>
      window.removeEventListener('vn2r:ribbon-collapsed-toggle', onToggle)
  }, [])

  // Hidden inputs for the FS-Access fallback (Firefox / Safari). Living
  // here means the ribbon owns the file-picker interaction end-to-end.
  const replaceInputRef = useRef<HTMLInputElement | null>(null)
  const addInputRef = useRef<HTMLInputElement | null>(null)

  const onTabClick = (tab: TabId) => {
    if (tab === activeTab && !collapsed) {
      // Second click on the active tab collapses (Revit/Office behavior).
      setCollapsed(true)
      persistCollapsed(true)
      return
    }
    setActiveTab(tab)
    persistTab(tab)
    if (collapsed) {
      setCollapsed(false)
      persistCollapsed(false)
    }
  }

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      persistCollapsed(!v)
      return !v
    })
  }

  if (!visible) return null

  return (
    <div
      data-tour="friso"
      className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-panel)] text-[var(--color-text)]"
    >
      {/* Tab strip */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-[var(--color-border-subtle)] px-2">
        <TabButton
          label="Ficheiro"
          active={activeTab === 'ficheiro' && !collapsed}
          onClick={() => onTabClick('ficheiro')}
        />
        <TabButton
          label="Vista"
          active={activeTab === 'vista' && !collapsed}
          onClick={() => onTabClick('vista')}
        />
        <TabButton
          label="Aparência"
          active={activeTab === 'aparencia' && !collapsed}
          onClick={() => onTabClick('aparencia')}
        />
        <TabButton
          label="Medições"
          active={activeTab === 'medicoes' && !collapsed}
          onClick={() => onTabClick('medicoes')}
        />
        <TabButton
          label="Anotações"
          active={activeTab === 'anotacoes' && !collapsed}
          onClick={() => onTabClick('anotacoes')}
        />
        <div className="ml-auto flex items-center gap-2 pr-1">
          <LoadingPill />
          <TourHost />
          <ShortcutsHelp />
          <button
            type="button"
            onClick={toggleCollapsed}
            title={
              collapsed
                ? 'Expandir ribbon (Ctrl+F1)'
                : 'Colapsar ribbon (Ctrl+F1)'
            }
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          >
            {collapsed ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronUp size={14} />
            )}
          </button>
        </div>
      </div>

      {/* Panels row (hidden when collapsed) */}
      {!collapsed && (
        <div className="flex min-h-[88px] items-stretch overflow-x-auto">
          {activeTab === 'ficheiro' && (
            <FicheiroTab
              replaceInputRef={replaceInputRef}
              addInputRef={addInputRef}
            />
          )}
          {activeTab === 'vista' && <VistaTab />}
          {activeTab === 'aparencia' && <AparenciaTab />}
          {activeTab === 'medicoes' && <MedicoesTab />}
          {activeTab === 'anotacoes' && <AnotacoesTab />}
        </div>
      )}

      {/* Hidden file inputs used by Firefox/Safari fallback */}
      <input
        ref={replaceInputRef}
        type="file"
        accept={modelAcceptAttr()}
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0]
          if (!file) return
          await viewerApi.loadIfcFile(file, { replace: true })
          e.target.value = ''
        }}
      />
      <input
        ref={addInputRef}
        type="file"
        accept={modelAcceptAttr()}
        multiple
        className="hidden"
        onChange={async (e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length === 0) return
          for (const file of files) {
            await viewerApi.loadIfcFile(file, { replace: false })
          }
          e.target.value = ''
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

interface FicheiroTabProps {
  replaceInputRef: React.RefObject<HTMLInputElement | null>
  addInputRef: React.RefObject<HTMLInputElement | null>
}

// "Abrir exemplo": for users without an IFC at hand. Single click loads
// the default small sample (replace mode); the ▾ opens a popover to pick a
// heavier model. Downloads stream from a public CORS-enabled host and reuse
// the normal loader pipeline (and its LoadingPill progress).
function SampleButton({ disabled }: { disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  const { busyId: busy, run: runSample } = useSampleLoad()
  const wrapRef = useRef<HTMLDivElement | null>(null)

  // O fecho por clique fora e por Escape passou para o RibbonMenu. Tinha
  // de sair daqui: o menu vive agora num portal, fora deste invólucro,
  // por isso este tratador via um clique DENTRO do menu como um clique
  // fora e fechava-o a meio da escolha.

  const run = (sample: (typeof SAMPLE_IFCS)[number]) => {
    setOpen(false)
    void runSample(sample)
  }

  // Copiar a ligação partilhável (abre o viewer já com este exemplo).
  // Útil para demos: partilha-se o link e o outro lado abre com modelo.
  const copyLink = async (id: string) => {
    try {
      await navigator.clipboard.writeText(
        sampleShareUrl(window.location.origin, id),
      )
      useAppStore.getState().pushToast('Ligação copiada', { type: 'success' })
    } catch {
      useAppStore
        .getState()
        .pushToast('Não foi possível copiar a ligação', { type: 'error' })
    }
  }

  return (
    <div ref={wrapRef} className="relative flex items-start">
      <RibbonButton
        icon={Building2}
        label={busy ? 'A abrir…' : 'Exemplo'}
        onClick={() => void run(DEFAULT_SAMPLE)}
        disabled={disabled || busy !== null}
        tooltip="Abrir um IFC de exemplo descarregado da internet (substitui os modelos atuais). Útil para testar o viewer sem ter um ficheiro à mão. Click no ▾ para escolher outro modelo."
      />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || busy !== null}
        title="Escolher o modelo de exemplo"
        aria-label="Escolher o modelo de exemplo"
        className="mt-1 rounded px-0.5 py-1 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
      >
        ▾
      </button>
      {open && (
        <RibbonMenu
          open={open}
          anchorRef={wrapRef}
          onClose={() => setOpen(false)}
          width={240}
          ariaLabel="Modelos de exemplo"
        >
          {SAMPLE_IFCS.map((s) => (
            <div key={s.id} className="flex items-stretch gap-1">
              <button
                type="button"
                onClick={() => void run(s)}
                className="flex min-w-0 flex-1 flex-col gap-0.5 rounded px-2 py-1.5 text-left hover:bg-[var(--color-bg)]"
              >
                <span className="flex items-center justify-between text-[11px] text-[var(--color-text)]">
                  <span>{s.label}</span>
                  <span className="font-mono text-[9px] text-[var(--color-text-dim)]">
                    {s.sizeLabel}
                  </span>
                </span>
                <span className="text-[10px] leading-snug text-[var(--color-text-muted)]">
                  {s.description}
                </span>
              </button>
              <button
                type="button"
                onClick={() => void copyLink(s.id)}
                title="Copiar ligação partilhável (abre o viewer já com este exemplo)"
                aria-label={`Copiar ligação para ${s.label}`}
                className="flex shrink-0 items-center rounded px-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
              >
                <LinkIcon size={13} />
              </button>
            </div>
          ))}
        </RibbonMenu>
      )}
    </div>
  )
}

function FicheiroTab({ replaceInputRef, addInputRef }: FicheiroTabProps) {
  const modelsCount = useAppStore((s) => s.models.length)
  // A nuvem de pontos vive aqui porque o gesto e importar um ficheiro,
  // como abrir um IFC. Estava na Cena por eu ter agrupado pelo sitio
  // onde a coisa aparece, e nao pelo que a pessoa vai fazer.
  const pointCloudPanelOpen = useAppStore((s) => s.pointCloudPanelOpen)
  const setPointCloudPanelOpen = useAppStore((s) => s.setPointCloudPanelOpen)
  const activeModelId = useAppStore((s) => s.activeModelId)
  const ifcDiffOpen = useAppStore((s) => s.ifcDiffOpen)
  const setIfcDiffOpen = useAppStore((s) => s.setIfcDiffOpen)
  const idsPanelOpen = useAppStore((s) => s.idsPanelOpen)
  const setIdsPanelOpen = useAppStore((s) => s.setIdsPanelOpen)
  const clashPanelOpen = useAppStore((s) => s.clashPanelOpen)
  const setClashPanelOpen = useAppStore((s) => s.setClashPanelOpen)
  const pdfReviewOpen = useAppStore((s) => s.pdfReviewOpen)
  const setPdfReviewOpen = useAppStore((s) => s.setPdfReviewOpen)
  const versionHistoryOpen = useAppStore((s) => s.versionHistoryOpen)
  const setVersionHistoryOpen = useAppStore((s) => s.setVersionHistoryOpen)
  const modelIntelligenceOpen = useAppStore((s) => s.modelIntelligenceOpen)
  const setModelIntelligenceOpen = useAppStore((s) => s.setModelIntelligenceOpen)
  const dadosPanelOpen = useAppStore((s) => s.dadosPanelOpen)
  const setDadosPanelOpen = useAppStore((s) => s.setDadosPanelOpen)
  const estruturaPanelOpen = useAppStore((s) => s.estruturaPanelOpen)
  const globoPanelOpen = useAppStore((s) => s.globoPanelOpen)
  const setGloboPanelOpen = useAppStore((s) => s.setGloboPanelOpen)
  const setEstruturaPanelOpen = useAppStore((s) => s.setEstruturaPanelOpen)
  const juntarIfcOpen = useAppStore((s) => s.juntarIfcOpen)
  const setJuntarIfcOpen = useAppStore((s) => s.setJuntarIfcOpen)
  const entregaAssinadaOpen = useAppStore((s) => s.entregaAssinadaOpen)
  const setEntregaAssinadaOpen = useAppStore((s) => s.setEntregaAssinadaOpen)
  const rawEnabled = useAppStore((s) => s.rawIfcEnabled)
  const setRawEnabled = useAppStore((s) => s.setRawIfcEnabled)
  // hasIfcLoader is not reactive on its own; viewerApi sets the
  // loader once useViewer's effect has bootstrapped Three.js. Subscribe
  // to viewerApi notifications so the Open IFC button enables itself
  // as soon as the loader is registered.
  const loaderReady = useSyncExternalStore(
    viewerApi.subscribe,
    viewerApi.hasIfcLoader,
  )

  const onPickReplace = async () => {
    if (isFsAccessSupported()) {
      const picked = await pickIfcFile()
      if (!picked) return
      await viewerApi.loadIfcFile(picked.file, {
        replace: true,
        handle: picked.handle,
      })
    } else {
      replaceInputRef.current?.click()
    }
  }
  const onPickAdd = async () => {
    if (isFsAccessSupported()) {
      const picked = await pickIfcFiles()
      if (picked.length === 0) return
      for (const item of picked) {
        await viewerApi.loadIfcFile(item.file, {
          replace: false,
          handle: item.handle,
        })
      }
    } else {
      addInputRef.current?.click()
    }
  }

  return (
    <>
      <RibbonPanel title="Modelo">
        <RibbonButton
          dataTour="abrir-modelo"
          icon={FolderOpen}
          label={modelsCount > 0 ? 'Substituir' : 'Abrir modelo'}
          onClick={onPickReplace}
          disabled={!loaderReady}
          tooltip={
            modelsCount > 0
              ? 'Descarrega os modelos atuais e abre um novo IFC ou Fragments'
              : 'Abrir um IFC, IFCZIP ou Fragments do disco'
          }
        />
        <RibbonButton
          icon={Plus}
          label="Adicionar"
          onClick={onPickAdd}
          disabled={!loaderReady}
          tooltip="Adicionar IFC ou Fragments à federação (mantém os modelos atuais e a vista da câmara)"
        />
        {isFeatureEnabled('nativeFragments') && (
          <RibbonButton
            icon={Download}
            label="Guardar .frag"
            onClick={() => {
              if (activeModelId) void viewerApi.downloadFragments(activeModelId)
            }}
            disabled={!activeModelId}
            tooltip="Guardar o modelo ativo no formato Fragments comprimido"
          />
        )}
        {isFeatureEnabled('pointClouds') && (
          <RibbonToggle
            icon={ScanLine}
            label="Point cloud"
            pressed={pointCloudPanelOpen}
            onClick={() => setPointCloudPanelOpen(!pointCloudPanelOpen)}
            tooltip="Importar uma nuvem de pontos (LAS, LAZ, E57, PLY, XYZ, PTS) para a cena e medir o desvio aproximado ao IFC. O LAS vai até à versão 1.3 do formato e o E57 até aos 80 MB. Aproximado: distância às caixas dos elementos, não uma inspeção mesh-to-cloud."
          />
        )}
        <RibbonPainelDaBarra id="ifcx" />
        <SampleButton disabled={!loaderReady} />
      </RibbonPanel>

      <RibbonPanel title="Comparação">
        <RibbonToggle
          icon={GitCompareArrows}
          label="Comparar IFC"
          pressed={ifcDiffOpen}
          onClick={() => setIfcDiffOpen(!ifcDiffOpen)}
          disabled={modelsCount < 2}
          tooltip={
            modelsCount < 2
              ? 'Carrega pelo menos dois IFC para comparar'
              : 'Comparar dois modelos IFC carregados'
          }
        />
        <RibbonToggle
          icon={ClipboardCheck}
          label="IDS"
          pressed={idsPanelOpen}
          onClick={() => setIdsPanelOpen(!idsPanelOpen)}
          disabled={modelsCount < 1}
          tooltip={
            modelsCount < 1
              ? 'Carrega um IFC para validar contra um IDS'
              : 'Validar o modelo contra um ficheiro IDS (buildingSMART): requisitos de informação (entidades, atributos, propriedades)'
          }
        />
        {isFeatureEnabled('versionHistory') && (
          <RibbonToggle
            icon={GitBranch}
            label="Versões"
            pressed={versionHistoryOpen}
            onClick={() => setVersionHistoryOpen(!versionHistoryOpen)}
            tooltip="Guardar marcos do modelo e reabri-los ao lado do atual. Cada marco guarda o IFC, para o Comparar IFC os poder ler."
          />
        )}
        <RibbonToggle
          icon={SquaresIntersect}
          label="Conflitos"
          pressed={clashPanelOpen}
          onClick={() => setClashPanelOpen(!clashPanelOpen)}
          disabled={modelsCount < 1}
          tooltip={
            modelsCount < 1
              ? 'Carrega um IFC para detetar conflitos'
              : 'Detetar conflitos de geometria entre dois conjuntos de elementos (por categorias, seleção ou regra), com realce dos dois elementos no 3D, triagem e exportação para BCF. O painel arrasta-se, redimensiona-se, maximiza-se e destaca-se para uma janela do browser. Com o módulo Conflitos ligado há também um separador próprio, que acopla por baixo do 3D.'
          }
        />
      </RibbonPanel>

      <RibbonPanel title="Ferramentas">
          <RibbonToggle
            icon={TableProperties}
            label="Dados IFC"
            pressed={dadosPanelOpen}
            onClick={() => setDadosPanelOpen(!dadosPanelOpen)}
            disabled={modelsCount === 0}
            tooltip="Inventário de Psets e propriedades (com tipo e unidade), acrescentar propriedades a partir de Excel, mudar o nome e apagar Psets, e gravar um IFC novo. Tudo no teu browser."
          />
          <RibbonToggle
            icon={Braces}
            label="Estrutura IFC"
            pressed={estruturaPanelOpen}
            onClick={() => setEstruturaPanelOpen(!estruturaPanelOpen)}
            disabled={modelsCount === 0}
            tooltip="Modo avançado: qualquer entidade do ficheiro (com ou sem geometria) por #id, GlobalId ou classe, com os atributos pelo esquema, as referências para navegar, quem a referencia e a linha STEP."
          />
          <RibbonToggle
            icon={Globe2}
            label="Globo 3D"
            pressed={globoPanelOpen}
            onClick={() => setGloboPanelOpen(!globoPanelOpen)}
            disabled={modelsCount === 0}
            tooltip="Os modelos georreferenciados num globo 3D, com relevo e a correcção do geoide (EGM96), para ver o edifício no sítio e à cota certos. Cesium, a correr no teu browser, sem contas nem chaves."
          />
          <RibbonToggle
            icon={Merge}
            label="Juntar IFC"
            pressed={juntarIfcOpen}
            onClick={() => setJuntarIfcOpen(!juntarIfcOpen)}
            tooltip="Juntar vários IFC num ficheiro só, sem os abrir no viewer. Motor do liteAECO (Apache-2.0), a correr no teu browser."
          />
          <RibbonToggle
            icon={FileSignature}
            label="Entrega assinada"
            pressed={entregaAssinadaOpen}
            onClick={() => setEntregaAssinadaOpen(!entregaAssinadaOpen)}
            tooltip="Termo de entrega com a impressão digital SHA-256 de cada ficheiro, para assinar no Autenticação.gov (Cartão de Cidadão ou Chave Móvel Digital), e verificação de uma entrega assinada. Os ficheiros não são alterados."
          />
          {isFeatureEnabled('pdfReview') && (
            <RibbonToggle
              icon={FileText}
              label="Rever PDF"
              pressed={pdfReviewOpen}
              onClick={() => setPdfReviewOpen(!pdfReviewOpen)}
              tooltip="Rever desenhos PDF: redlines, medições calibradas, pins, comparação de revisões. Os documentos não saem do browser."
            />
          )}
          <RibbonPainelDaBarra id="documento" />
          {(isFeatureEnabled('graphRag') || isFeatureEnabled('circularity')) && (
            <RibbonToggle
              icon={Network}
              label="Grafo / Reuso"
              pressed={modelIntelligenceOpen}
              onClick={() => setModelIntelligenceOpen(!modelIntelligenceOpen)}
              disabled={modelsCount === 0}
              tooltip="Consultar o grafo local do modelo e medir quanta documentação existe para reutilização. Não prova circularidade: conta campos preenchidos."
            />
          )}
          {isFeatureEnabled('mcpServer') && (
            <RibbonButton
              icon={Server}
              label="Snapshot MCP"
              onClick={() => void downloadMcpSnapshot()}
              disabled={modelsCount === 0}
              tooltip="Exportar uma fotografia do modelo, só de leitura, para o Claude Code ou outro cliente MCP ler. Leva as propriedades em texto: trata o ficheiro como sensível."
            />
          )}
      </RibbonPanel>

      <RibbonPanel title="Parser">
        <RibbonSmallToggle
          icon={Database}
          label="Parser IFC paralelo"
          pressed={rawEnabled}
          onClick={() => setRawEnabled(!rawEnabled)}
          tooltip="Mantém o IFC aberto em paralelo via web-ifc para inspecção de IfcLocalPlacement, IfcShapeRepresentation, IfcMappedItem. Custa memória e segundos no load."
        />
      </RibbonPanel>
    </>
  )
}

function VistaTab() {
  const viewerSurface = useAppStore((s) => s.viewerSurface)
  const setViewerSurface = useAppStore((s) => s.setViewerSurface)
  const viewpointsPanelOpen = useAppStore((s) => s.viewpointsPanelOpen)
  const setViewpointsPanelOpen = useAppStore((s) => s.setViewpointsPanelOpen)
  const viewpointCount = useAppStore((s) => s.viewpoints.length)
  // Um pin e um topico BCF com viewpoint: pertence ao lado dos
  // viewpoints, nao ao modo de desenhar o modelo.
  const bcfPinsPanelOpen = useAppStore((s) => s.bcfPinsPanelOpen)
  const setBcfPinsPanelOpen = useAppStore((s) => s.setBcfPinsPanelOpen)
  const mapPanelOpen = useAppStore((s) => s.mapPanelOpen)
  const setMapPanelOpen = useAppStore((s) => s.setMapPanelOpen)
  const setMapToolsOpen = useAppStore((s) => s.setMapToolsOpen)
  const vistaModelCount = useAppStore((s) => s.models.length)
  const sectionEnabled = useAppStore((s) => s.sectionEnabled)
  const alternarSectionBox = useAppStore((s) => s.alternarSectionBox)
  const sectionPanel = useAppStore((s) => s.sectionPanel)
  const bearingPick = useAppStore((s) => s.bearingPick)
  const setBearingPick = useAppStore((s) => s.setBearingPick)
  const marqueeMode = useAppStore((s) => s.marqueeMode)
  const setMarqueeMode = useAppStore((s) => s.setMarqueeMode)
  const selectionCount = useAppStore((s) => s.selectedTargets.length)
  const clearSelection = useAppStore((s) => s.clearSelection)
  const hiddenCount = useAppStore((s) => s.hiddenCount)
  const visibilityMode = useAppStore((s) => s.visibilityMode)
  const filterPanelOpen = useAppStore((s) => s.filterPanelOpen)
  const setFilterPanelOpen = useAppStore((s) => s.setFilterPanelOpen)
  const hideSpatialVolumes = useAppStore((s) => s.hideSpatialVolumes)

  // Abre o painel Mapa e faz scroll ao bloco de federação (que só
  // aparece com 2+ modelos). O painel pode ainda estar a montar, daí o
  // pequeno atraso.

  // Abre o painel Mapa e faz scroll ao bloco de federação (que só
  // aparece com 2+ modelos). O painel pode ainda estar a montar, daí o
  // pequeno atraso.
  const openFederation = () => {
    setMapPanelOpen(true)
    // O bloco de federação vive no acordeão "Ferramentas": abre-o, senão
    // o alvo do scroll está recolhido.
    setMapToolsOpen(true)
    window.setTimeout(() => {
      document
        .getElementById('vn2r-federation-block')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 250)
  }

  return (
    <>
      <RibbonPanel title="Câmara">
        <RibbonButton
          icon={Focus}
          label="Focar"
          onClick={() => void viewerApi.focusOnSelection()}
          disabled={selectionCount === 0}
          tooltip="Centrar e pivotar a câmara na seleção (F). Sem seleção, enquadra todos os modelos."
        />
        <RibbonToggle
          icon={Box}
          label="Section box"
          pressed={sectionEnabled}
          onClick={alternarSectionBox}
          tooltip={
            sectionEnabled && sectionPanel === 'fechado'
              ? 'Reabre o painel da section box (a caixa está ligada). Um segundo clique desliga-a.'
              : 'Ativa/desativa o section box'
          }
        />
        <RibbonPainelDaBarra id="planos-corte" />
        {/* Estava só num botão minúsculo do cubo de navegação, com um
            símbolo por etiqueta: ninguém o encontrava. Um comando com
            nome pertence ao friso. */}
        <RibbonToggle
          icon={Ruler}
          label="Alinhar vista"
          pressed={bearingPick !== null}
          onClick={() => {
            if (bearingPick === null) clearSelection()
            setBearingPick(bearingPick ? null : [])
          }}
          tooltip="Clica numa fachada e a vista roda para ela ficar horizontal no ecrã, com a face do cubo alinhada. Basta um clique: a direcção vem da própria face, portanto não depende de onde clicaste. Move a câmara; não toca no modelo nem na georreferenciação."
        />
      </RibbonPanel>

      {/* As vistas vivem no separador que se chama Vista. Estiveram
          na Cena por um engano meu de arrumacao: o utilizador chama
          "vista" a isto, nao ao modo de desenhar o modelo. */}
      <RibbonPanel title="Vistas">
        <RibbonToggle
          icon={DraftingCompass}
          label="Vistas 2D"
          pressed={viewerSurface === '2d'}
          onClick={() =>
            setViewerSurface(viewerSurface === '2d' ? '3d' : '2d')
          }
          disabled={vistaModelCount < 1}
          tooltip={
            vistaModelCount < 1
              ? 'Carrega um IFC para gerar plantas e cortes'
              : 'Entra no MODO 2D: o desenho passa a ocupar a área central, com o navegador de vistas à esquerda e as propriedades à direita. Plantas por piso e cortes (da caixa de corte, por azimute ou desenhados), exportação para PDF, DXF e SVG. O comutador 3D | 2D no canto da área central faz o mesmo. As vistas ficam gravadas neste browser, por modelo.'
          }
        />
        <RibbonToggle
          icon={Camera}
          label="Viewpoints"
          pressed={viewpointsPanelOpen}
          onClick={() => setViewpointsPanelOpen(!viewpointsPanelOpen)}
          badge={viewpointCount > 0 ? String(viewpointCount) : null}
          tooltip="Painel de viewpoints. Captura a vista atual (câmara, secção, seleção, escondidos, transparências) com thumbnail. Persistido em IndexedDB local, não sai do teu computador."
        />
        {isFeatureEnabled('bcfPins') && (
          <RibbonToggle
            icon={MapPin}
            label="Pins BCF"
            pressed={bcfPinsPanelOpen}
            onClick={() => setBcfPinsPanelOpen(!bcfPinsPanelOpen)}
            tooltip="Ligar, desligar e filtrar os marcadores dos tópicos BCF sobre o modelo. Um tópico só ganha marcador depois de lhe guardares um viewpoint neste browser."
          />
        )}
        <RibbonToggle
          icon={MapIcon}
          label="Mapa"
          pressed={mapPanelOpen}
          onClick={() => setMapPanelOpen(!mapPanelOpen)}
          tooltip="Mostrar o modelo ativo num mapa OpenStreetMap: origem e pegada nas coordenadas do IfcMapConversion, convertidas do CRS (EPSG) para latitude/longitude. Inclui reposicionar com clique no mapa, chão OSM no 3D (com relevo), verificação de elevação contra o terreno real e, com 2+ modelos, a verificação de alinhamento da federação (secção no fundo do painel). Requer CRS conhecido e internet para os tiles."
        />
        <RibbonToggle
          icon={GitCompareArrows}
          label="Federação"
          pressed={false}
          onClick={openFederation}
          disabled={vistaModelCount < 2}
          tooltip={
            vistaModelCount < 2
              ? 'Carrega 2+ modelos para verificar se estão federados no sítio certo'
              : 'Abre o painel Mapa na verificação de alinhamento entre modelos (federação)'
          }
        />
      </RibbonPanel>

      <RibbonPanel title="Seleção">
        <RibbonToggle
          icon={MousePointerSquareDashed}
          label="Janela"
          pressed={marqueeMode}
          onClick={() => setMarqueeMode(!marqueeMode)}
          tooltip="Forçar modo janela: drag em qualquer sítio inicia seleção (sem este modo, drag em zona vazia já inicia automaticamente). Esc cancela."
        />
        <RibbonButton
          icon={ArrowLeftRight}
          label="Inverter"
          onClick={() => void viewerApi.invertSelection()}
          tooltip="Substituir a seleção pelos elementos visíveis que NÃO estão selecionados (Shift+I)"
        />
        <RibbonButton
          icon={Eraser}
          label="Limpar"
          onClick={clearSelection}
          disabled={selectionCount === 0}
          tooltip="Limpar a seleção atual (Esc)"
        />
        <RibbonToggle
          icon={Filter}
          label="Filtro"
          pressed={filterPanelOpen}
          onClick={() => setFilterPanelOpen(!filterPanelOpen)}
          tooltip="Painel de filtro multi-critério (entidade, storey, propriedade, material, tipo, sistema, predefinedType) combinados com AND/OR. Aplicar selecciona os elementos que correspondem; depois usa Esconder/Isolar/Aparência para agir sobre eles."
        />
      </RibbonPanel>

      <RibbonPanel title="Visibilidade">
        <RibbonToggle
          icon={EyeOff}
          label="Esconder"
          pressed={visibilityMode === 'hide'}
          onClick={() => void viewerApi.hideSelected()}
          disabled={selectionCount === 0}
          tooltip={
            visibilityMode === 'hide'
              ? 'Já há elementos escondidos por hide; novo click adiciona a seleção atual ao conjunto escondido. Usa "Mostrar tudo" para repor.'
              : 'Esconder seleção (H). Aditivo: cada click acumula a seleção atual ao conjunto escondido.'
          }
        />
        <RibbonToggle
          icon={Target}
          label="Isolar"
          pressed={visibilityMode === 'isolate'}
          onClick={() => void viewerApi.isolateSelected()}
          disabled={selectionCount === 0}
          tooltip={
            visibilityMode === 'isolate'
              ? 'Já em modo isolar; novo click refina para a seleção atual (esconde tudo o resto, incluindo coisas que estavam visíveis). Esconder também funciona dentro do isolar. Mostrar tudo repõe.'
              : 'Isolar seleção: esconde tudo o resto (I). Posteriormente podes esconder mais ou refinar isolando uma sub-seleção.'
          }
        />
        <RibbonButton
          icon={Eye}
          label="Mostrar tudo"
          onClick={() => void viewerApi.showAll()}
          disabled={hiddenCount === 0}
          badge={hiddenCount > 0 ? String(hiddenCount) : null}
          tooltip={
            hiddenCount === 0
              ? 'Não há elementos escondidos'
              : `Mostrar todos (${hiddenCount} escondidos), Alt+H`
          }
        />
        <RibbonToggle
          icon={BoxIcon}
          label="Esconder spaces"
          pressed={hideSpatialVolumes}
          onClick={() => void viewerApi.setSpatialVolumesHidden(!hideSpatialVolumes)}
          tooltip="Esconder em 3D as caixas de IfcSpace / IfcZone / IfcSpatialZone (mantém-se na árvore espacial e na Tabela). Desliga para ver os volumes."
        />
      </RibbonPanel>

      <RibbonPanel title="Painéis">
        <RibbonPainelDaBarra id="arvore" />
        <RibbonPainelDaBarra id="propriedades" />
      </RibbonPanel>
    </>
  )
}

// ---------------------------------------------------------------------------
// AparenciaTab: como o modelo esta desenhado. Nasceu como "Cena",
// nome que competia com "Vista" e nao dizia a ninguem o que la
// estava. Passou a Aparencia, e as vistas, os pins BCF e a nuvem de
// pontos foram para onde pertencem.
// ---------------------------------------------------------------------------

function AparenciaTab() {
  const appearancePanelOpen = useAppStore((s) => s.appearancePanelOpen)
  const setAppearancePanelOpen = useAppStore((s) => s.setAppearancePanelOpen)
  const gridVisible = useAppStore((s) => s.gridVisible)
  const edgesVisible = useAppStore((s) => s.edgesVisible)
  const classColorsEnabled = useAppStore((s) => s.classColorsEnabled)
  const setClassColorsEnabled = useAppStore((s) => s.setClassColorsEnabled)
  const colorByPanelOpen = useAppStore((s) => s.colorByPanelOpen)
  const setColorByPanelOpen = useAppStore((s) => s.setColorByPanelOpen)
  const bgMode = useAppStore((s) => s.bgMode)
  const estiloApresentacao = useAppStore((s) => s.estiloApresentacao)
  const ghostMode = useAppStore((s) => s.ghostMode)
  const ghostOpacity = useAppStore((s) => s.ghostOpacity)
  const ghostEstilo = useAppStore((s) => s.ghostEstilo)
  const selecaoAtravessa = useAppStore((s) => s.selecaoAtravessa)
  const setSelecaoAtravessa = useAppStore((s) => s.setSelecaoAtravessa)
  const levelsOverlay = useAppStore((s) => s.levelsOverlay)
  const setLevelsOverlay = useAppStore((s) => s.setLevelsOverlay)
  const gridsOverlay = useAppStore((s) => s.gridsOverlay)
  const setGridsOverlay = useAppStore((s) => s.setGridsOverlay)
  const originMarkerOverlay = useAppStore((s) => s.originMarkerOverlay)
  const setOriginMarkerOverlay = useAppStore((s) => s.setOriginMarkerOverlay)

  return (
    <>
      <RibbonPanel title="Desenho">
        <RibbonToggle
          icon={Sparkles}
          label="Aparência"
          pressed={appearancePanelOpen}
          onClick={() => setAppearancePanelOpen(!appearancePanelOpen)}
          tooltip="Janela flutuante com pintura custom, ghost (contexto translúcido) e transparência por opacidade. Mantém-se aberta enquanto clicas elementos."
        />
        <RibbonToggle
          icon={Spline}
          label="Arestas"
          pressed={edgesVisible}
          onClick={() => viewerApi.setEdgesVisible(!edgesVisible)}
          tooltip="Mostrar/esconder as arestas geradas a partir da geometria (Three EdgesGeometry, threshold 30°). Aviso: incluem arestas internas e seams de tessellation, pelo que podem aparecer linhas que não correspondem a contornos visíveis. Auto-desativam quando há elementos escondidos."
        />
        <RibbonToggle
          icon={Palette}
          label="Cores por classe"
          pressed={classColorsEnabled}
          onClick={() => {
            setClassColorsEnabled(!classColorsEnabled)
            void viewerApi.repaintAllModels({ reset: true })
          }}
          tooltip="Pintar elementos pela classe IFC (HVAC laranja, ducts amarelo, paredes cinzentas). Desligado: usa os IfcSurfaceStyle / IfcMaterial nativos do IFC. IFCs sem styles ficam brancos."
        />
        <RibbonToggle
          icon={Sparkles}
          label="Colorir por…"
          pressed={colorByPanelOpen}
          onClick={() => setColorByPanelOpen(!colorByPanelOpen)}
          tooltip="Painel para colorir elementos por critério: entidade IFC, PredefinedType, sistema/grupo, material, tipo (IfcRelDefinesByType) ou valor de uma propriedade. Cada bucket distinto recebe uma cor; a legenda fica visível no painel."
        />
        <RibbonPainelDaBarra id="lentes" />
        <RibbonToggle
          icon={bgMode === 'light' ? Sun : Moon}
          label={bgMode === 'light' ? 'Fundo claro' : 'Fundo escuro'}
          pressed={bgMode === 'light'}
          onClick={() => viewerApi.setBgMode(bgMode === 'light' ? 'dark' : 'light')}
          tooltip="Alternar fundo da cena entre escuro (default BIM) e claro (screenshots / docs)."
        />
        <RibbonToggle
          icon={Presentation}
          label="Apresentação"
          pressed={estiloApresentacao}
          onClick={() => void viewerApi.setEstiloApresentacao(!estiloApresentacao)}
          tooltip="Estilo para imagens e vídeos: fundo em degradê e iluminação mais suave. Liga o fundo claro."
        />
        <RibbonToggle
          icon={Ghost}
          label="Ghost"
          pressed={ghostMode}
          onClick={() => void viewerApi.setGhostMode(!ghostMode)}
          tooltip="Modo ghost: a selecção fica sólida e o resto do modelo passa a contornos (ou translúcido), sem a tapar. Atalho G. O estilo escolhe-se no botão ao lado."
        />
        <GhostOpacityButton enabled={ghostMode} opacity={ghostOpacity} estilo={ghostEstilo} />
        <RibbonToggle
          icon={ScanEye}
          label="Ver através"
          pressed={selecaoAtravessa}
          onClick={() => setSelecaoAtravessa(!selecaoAtravessa)}
          tooltip="Desenhar a selecção por cima de tudo, com um véu e as arestas: um elemento tapado por outros (uma conduta atrás do tecto falso) continua a ver-se. Até 400 elementos seleccionados."
        />
      </RibbonPanel>

      <RibbonPanel title="Referências">
        <RibbonToggle
          icon={Grid3x3}
          label="Grelha"
          pressed={gridVisible}
          onClick={() => viewerApi.setGridVisible(!gridVisible)}
          tooltip="Mostrar/esconder a grelha do chão"
        />
        <RibbonToggle
          icon={Layers}
          label="Levels"
          pressed={levelsOverlay}
          wip
          onClick={() => setLevelsOverlay(!levelsOverlay)}
          tooltip="Linha azul horizontal a cada IfcBuildingStorey.Elevation com etiqueta do nome do piso. Equivalente a 'Levels' no Revit."
        />
        <RibbonToggle
          icon={Grid3x3}
          label="Grids IFC"
          pressed={gridsOverlay}
          wip
          onClick={() => setGridsOverlay(!gridsOverlay)}
          tooltip="Linhas amarelas para IfcGrid.UAxes/VAxes com etiquetas IfcGridAxis.AxisTag. Distinto da grelha do chão. Carrega no filtro ao lado para escolher quais IfcBuildingStoreys mostram grids."
        />
        <GridsStoreyFilterButton enabled={gridsOverlay} />
        <RibbonToggle
          icon={LocateFixed}
          label="Origem"
          pressed={originMarkerOverlay}
          onClick={() => {
            // O efeito de overlays (acima) aplica/limpa em reação ao
            // estado; chamar aqui também criava uma corrida async.
            setOriginMarkerOverlay(!originMarkerOverlay)
          }}
          tooltip="Marca a origem verdadeira do ficheiro de cada modelo (incluindo o recentramento do conversor e o MapConversion/override) com um tripé de eixos E/N/h, e as origens dos nós da hierarquia espacial com tripés pequenos. Clica na etiqueta da origem de um modelo para abrir o editor de posição. Usa o filtro ao lado para escolher que origens mostrar e para ligar/desligar as etiquetas."
        />
        <OriginKindsFilterButton enabled={originMarkerOverlay} />
      </RibbonPanel>
    </>
  )
}

// ---------------------------------------------------------------------------
// MedicoesTab: full row of measurement controls. Lives in its own
// ribbon tab so the Vista tab stays focused on visibility / camera.
// ---------------------------------------------------------------------------

function MedicoesTab() {
  return (
    <>
      <MedicoesPanel />
    </>
  )
}

// ---------------------------------------------------------------------------
// AnotacoesTab: text labels, leaders, revision clouds layered over
// the model. Independent from measurements.
// ---------------------------------------------------------------------------

function AnotacoesTab() {
  const placeMode = useAppStore((s) => s.annotationPlaceMode)
  const setPlaceMode = useAppStore((s) => s.setAnnotationPlaceMode)
  const setMeasureMode = useAppStore((s) => s.setMeasureMode)
  const setMeasureDeleteMode = useAppStore((s) => s.setMeasureDeleteMode)
  const annotations = useAppStore((s) => s.annotations)
  const clearAll = useAppStore((s) => s.clearAnnotations)
  const removeOne = useAppStore((s) => s.removeAnnotation)

  const togglePlace = (m: 'text' | 'leader' | 'cloud' | 'rect' | 'coord') => {
    const next = placeMode === m ? null : m
    if (next !== null) {
      // Mutual exclusion: turning on an annotation mode cancels any
      // active measure / delete mode (they all compete for canvas
      // clicks; running two at once produces silent no-ops).
      setMeasureMode(null)
      setMeasureDeleteMode(false)
    }
    setPlaceMode(next)
  }

  const totalCount = annotations.length

  return (
    <>
      <RibbonPanel title="Colocar">
        <RibbonToggle
          icon={Type}
          label="Texto"
          pressed={placeMode === 'text'}
          onClick={() => togglePlace('text')}
          tooltip="Colocar uma etiqueta de texto. Click no modelo no sítio onde queres a etiqueta. Aparece um prompt para escreveres o texto."
        />
        <RibbonToggle
          icon={ArrowUpRight}
          label="Seta"
          pressed={placeMode === 'leader'}
          onClick={() => togglePlace('leader')}
          tooltip="Seta com texto opcional. 1º click define a origem da seta (onde fica o texto), 2º click define a ponta. Esc cancela."
        />
        <RibbonToggle
          icon={Square}
          label="Rectângulo"
          pressed={placeMode === 'rect'}
          onClick={() => togglePlace('rect')}
          tooltip="Rectângulo de marcação. 1º click e 2º click definem dois cantos. O rectângulo é desenhado no plano de trabalho activo (Medições → Plano) ou no plano horizontal por defeito."
        />
        <RibbonToggle
          icon={Cloud}
          label="Nuvem"
          pressed={placeMode === 'cloud'}
          onClick={() => togglePlace('cloud')}
          tooltip="Nuvem de revisão (mais ornamentada que o Rectângulo). 1º e 2º click definem dois cantos. Aparece um prompt para a etiqueta (ex.: R1). Desenhada no plano de trabalho activo, se houver."
        />
      </RibbonPanel>

      <RibbonPanel title="Plano">
        <WorkPlaneControl />
      </RibbonPanel>

      <RibbonPanel title="Lista">
        <RibbonButton
          icon={Eraser}
          label="Limpar tudo"
          onClick={() => {
            if (totalCount === 0) return
            if (
              window.confirm(
                `Apagar todas as ${totalCount} anotações? Esta operação não tem undo.`,
              )
            ) {
              clearAll()
            }
          }}
          disabled={totalCount === 0}
          badge={totalCount > 0 ? String(totalCount) : null}
          tooltip={
            totalCount === 0
              ? 'Não há anotações para apagar'
              : `Apagar todas as ${totalCount} anotações`
          }
        />
        <AnnotationsListPopover
          annotations={annotations}
          onRemove={removeOne}
        />
      </RibbonPanel>
    </>
  )
}

interface AnnotationsListPopoverProps {
  annotations: ReturnType<typeof useAppStore.getState>['annotations']
  onRemove: (id: string) => void
}

function AnnotationsListPopover({
  annotations,
  onRemove,
}: AnnotationsListPopoverProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={annotations.length === 0}
        title={
          annotations.length === 0
            ? 'Não há anotações'
            : 'Lista de anotações (click no × para apagar uma)'
        }
        className={`flex w-[68px] flex-col items-center gap-1 rounded px-1 py-1 text-center text-[10px] transition-colors ${
          annotations.length === 0
            ? 'cursor-not-allowed text-[var(--color-text-dim)] opacity-50'
            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
        }`}
      >
        <Trash2 size={22} strokeWidth={1.5} />
        <span className="leading-tight">Gerir</span>
      </button>
      {open && annotations.length > 0 && (
        <RibbonMenu
          open={open && annotations.length > 0}
          anchorRef={wrapRef}
          onClose={() => setOpen(false)}
          width={260}
          maxHeight={300}
          ariaLabel="Anotações"
        >
          {annotations.map((a) => {
            const label =
              a.kind === 'text'
                ? a.text
                : a.kind === 'leader'
                  ? `Seta${a.text ? `: ${a.text}` : ''}`
                  : a.kind === 'cloud'
                    ? `Nuvem${a.tag ? ` ${a.tag}` : ''}`
                    : a.kind === 'rect'
                      ? 'Rectângulo'
                      : `Coord (${a.units})`
            return (
              <div
                key={a.id}
                className="flex items-center gap-2 rounded px-2 py-1 text-[11px] hover:bg-[var(--color-bg)]"
              >
                <span className="flex-1 truncate text-[var(--color-text-muted)]">
                  {label}
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(a.id)}
                  title="Apagar esta anotação"
                  className="rounded px-1 text-[var(--color-text-dim)] hover:text-red-400"
                >
                  ×
                </button>
              </div>
            )
          })}
        </RibbonMenu>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// MedicoesPanel: distance, edge, area, coordinates HUD, visibility,
// units selector, clear button. Tools live in @viewer/measure (ThatOpen
// LengthMeasurement + AreaMeasurement). State is in measureSlice.
// ---------------------------------------------------------------------------

function MedicoesPanel() {
  const measureMode = useAppStore((s) => s.measureMode)
  const setMeasureMode = useAppStore((s) => s.setMeasureMode)
  const measurementsVisible = useAppStore((s) => s.measurementsVisible)
  const setMeasurementsVisible = useAppStore((s) => s.setMeasurementsVisible)
  const setShowCoordinates = useAppStore((s) => s.setShowCoordinates)
  const measureUnits = useAppStore((s) => s.measureUnits)
  const setMeasureUnits = useAppStore((s) => s.setMeasureUnits)
  const measureDeleteMode = useAppStore((s) => s.measureDeleteMode)
  const setMeasureDeleteMode = useAppStore((s) => s.setMeasureDeleteMode)
  const annotationPlaceMode = useAppStore((s) => s.annotationPlaceMode)
  const setAnnotationPlaceMode = useAppStore(
    (s) => s.setAnnotationPlaceMode,
  )
  // Coord annotations also count as "medições" for the user: Limpar
  // and the badge include them.
  const coordCount = useAppStore(
    (s) => s.annotations.filter((a) => a.kind === 'coord').length,
  )
  const annotations = useAppStore((s) => s.annotations)
  const removeAnnotation = useAppStore((s) => s.removeAnnotation)

  // Measurement count comes from the imperative singleton because the
  // ThatOpen DataSet drives it; mirror to React via subscribe so the
  // badge and "Limpar" disabled state stay in sync.
  const count = useSyncExternalStore(
    measureApi.subscribe,
    measureApi.getCount,
  )

  const toggle = (target: MeasureMode) => {
    const next = measureMode === target ? null : target
    if (next !== null) setAnnotationPlaceMode(null)
    setMeasureMode(next)
  }
  const toggleDelete = () => {
    const next = !measureDeleteMode
    if (next) setAnnotationPlaceMode(null)
    setMeasureDeleteMode(next)
  }
  // Coordenadas: combines two behaviours behind a single toggle:
  // (a) shows the live HUD readout in the bottom-left corner,
  // (b) puts the canvas in coord-place mode so each click on the
  // model drops a fixed coord annotation at the picked point.
  const coordActive = annotationPlaceMode === 'coord'
  const toggleCoord = () => {
    const next = !coordActive
    if (next) {
      setMeasureMode(null)
      setMeasureDeleteMode(false)
      setShowCoordinates(true)
      setAnnotationPlaceMode('coord')
    } else {
      setAnnotationPlaceMode(null)
      // Leave the HUD on if the user had it on independently; today
      // we treat the two as joined, so turn it off too.
      setShowCoordinates(false)
    }
  }

  return (
    <RibbonPanel title="Medições">
      <RibbonToggle
        icon={Ruler}
        label="Distância"
        pressed={measureMode === 'length-free'}
        onClick={() => toggle('length-free')}
        tooltip="Medir distância entre dois pontos. Click para o 1º ponto, click para o 2º; a cota fica visível. Esc cancela e desliga o modo."
      />
      <RibbonToggle
        icon={Spline}
        label="Aresta"
        pressed={measureMode === 'length-edge'}
        onClick={() => toggle('length-edge')}
        tooltip="Medir uma aresta do modelo. Aproxima o cursor da aresta para fazer snap, click para confirmar."
      />
      <RibbonToggle
        icon={Square}
        label="Área"
        pressed={
          measureMode === 'area-free' ||
          measureMode === 'area-square' ||
          measureMode === 'area-face'
        }
        onClick={() =>
          toggle(measureMode === 'area-free' ? null : 'area-free')
        }
        tooltip="Medir uma área por polígono. Click para cada vértice, duplo-click ou Enter fecha o polígono. Esc cancela."
      />
      <RibbonToggle
        icon={Crosshair}
        label="Coordenadas"
        pressed={coordActive}
        onClick={toggleCoord}
        tooltip="Activa o modo coordenadas: HUD em baixo-esquerda mostra X/Y/Z do cursor, e cada click no modelo deixa uma etiqueta fixa com as coordenadas absolutas (e relativas, se houver origem definida no HUD). Esc sai do modo."
      />
      <RibbonToggle
        icon={measurementsVisible ? Eye : EyeOff}
        label={measurementsVisible ? 'Cotas' : 'Ocultas'}
        pressed={measurementsVisible}
        onClick={() => setMeasurementsVisible(!measurementsVisible)}
        tooltip="Mostrar/esconder todas as cotas e áreas medidas (sem as apagar)."
      />
      <UnitsControl units={measureUnits} onChange={setMeasureUnits} />
      <WorkPlaneControl />
      <RibbonToggle
        icon={Trash2}
        label="Apagar uma"
        pressed={measureDeleteMode}
        onClick={toggleDelete}
        disabled={count + coordCount === 0}
        tooltip={
          count + coordCount === 0
            ? 'Não há medições nem etiquetas de coordenadas para apagar'
            : 'Modo apagar individual: click no traço da cota, na área, ou na etiqueta de coordenadas para a remover. Esc sai do modo.'
        }
      />
      <RibbonButton
        icon={Eraser}
        label="Limpar"
        onClick={() => {
          const total = count + coordCount
          if (total === 0) return
          if (
            window.confirm(
              `Apagar todas as ${total} medições (incluindo ${coordCount} etiquetas de coordenadas)? Sem undo.`,
            )
          ) {
            measureApi.clearAll()
            // Remove only coord annotations; text/leader/cloud/rect
            // belong to Anotações and have their own Limpar.
            for (const a of annotations) {
              if (a.kind === 'coord') removeAnnotation(a.id)
            }
          }
        }}
        disabled={count + coordCount === 0}
        badge={count + coordCount > 0 ? String(count + coordCount) : null}
        tooltip={
          count + coordCount === 0
            ? 'Não há medições para apagar'
            : `Apagar todas as ${count + coordCount} medições (cotas + coordenadas)`
        }
      />
    </RibbonPanel>
  )
}

// Work plane button + popover. Pressed when a plane is set; the
// popover offers XY/XZ/YZ at world origin, "Picar face" mode, and
// "Remover" to clear. Visual scaffold only; measurement clicks are
// not constrained to the plane (deferred until we hook into the
// ThatOpen pick logic). The plane gives the user a concrete 3D
// reference so they can verify they're measuring on the right face.
function WorkPlaneControl() {
  const workPlane = useAppStore((s) => s.workPlane)
  const setWorkPlane = useAppStore((s) => s.setWorkPlane)
  const pickMode = useAppStore((s) => s.workPlanePickMode)
  const setPickMode = useAppStore((s) => s.setWorkPlanePickMode)
  const snap = useAppStore((s) => s.workPlaneSnap)
  const setSnap = useAppStore((s) => s.setWorkPlaneSnap)
  const planeVisible = useAppStore((s) => s.workPlaneVisible)
  const setPlaneVisible = useAppStore((s) => s.setWorkPlaneVisible)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)


  const setAxis = (kind: 'XY' | 'XZ' | 'YZ') => {
    const normals: Record<typeof kind, [number, number, number]> = {
      XY: [0, 1, 0],
      XZ: [0, 0, 1],
      YZ: [1, 0, 0],
    }
    // Position the plane near the model the user is actually looking
    // at. Anchoring at world origin meant geocoded models had the
    // plane drawn miles off-screen ("plano não faz nada"). For XY
    // we sit on the model's floor (bbox.min.y); for XZ/YZ we sit
    // through the bbox centre.
    const center = computeModelsBboxCenter()
    const minY = computeModelsMinY()
    const origin: [number, number, number] =
      kind === 'XY'
        ? [center[0], minY, center[2]]
        : kind === 'XZ'
          ? [center[0], center[1], center[2]]
          : [center[0], center[1], center[2]]
    setWorkPlane({ kind, origin, normal: normals[kind] })
    setOpen(false)
  }

  const labels: Record<WorkPlaneKind, string> = {
    XY: 'XY (planta)',
    XZ: 'XZ (frontal)',
    YZ: 'YZ (lateral)',
    face: 'Face picada',
  }

  return (
    <div
      ref={wrapRef}
      className="relative flex w-[68px] flex-col items-center px-1 pt-1"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-pressed={workPlane !== null}
        title={
          workPlane
            ? `Plano de trabalho: ${labels[workPlane.kind]}. Click para mudar ou remover.`
            : 'Definir plano de trabalho (referência visual no espaço 3D para orientar as medições)'
        }
        className={`flex w-full flex-col items-center gap-1 rounded px-1 py-1 text-center text-[10px] transition-colors ${
          workPlane
            ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent-border)]'
            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
        }`}
      >
        <Layers size={22} strokeWidth={1.5} />
        <span className="leading-tight">Plano</span>
      </button>
      {open && (
        <RibbonMenu
          open={open}
          anchorRef={wrapRef}
          onClose={() => setOpen(false)}
          width={200}
          align="center"
          ariaLabel="Plano de trabalho"
        >
          <div className="px-2 pb-1 pt-1 text-[9px] uppercase tracking-wider text-[var(--color-text-muted)]">
            Plano de trabalho
          </div>
          {(['XY', 'XZ', 'YZ'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setAxis(k)}
              className={`flex w-full items-center justify-between rounded px-2 py-1 text-[11px] ${
                workPlane?.kind === k && !pickMode
                  ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                  : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
              }`}
            >
              <span>{labels[k]}</span>
              <span className="font-mono text-[9px] text-[var(--color-text-dim)]">
                @ origem
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setPickMode(!pickMode)
              setOpen(false)
            }}
            className={`mt-1 flex w-full items-center gap-2 rounded border-t border-[var(--color-border)]/60 px-2 py-1 text-[11px] ${
              pickMode
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
            }`}
            title="Após activar, clica numa face do modelo. O plano fica alinhado com essa face."
          >
            <Hand size={12} />
            <span>{pickMode ? 'A picar face… (Esc cancela)' : 'Picar face'}</span>
          </button>
          {workPlane && (
            <label
              className="mt-1 flex w-full cursor-pointer items-center justify-between gap-2 rounded border-t border-[var(--color-border)]/60 px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              title="Quando ligado, todas as anotações e medições são projectadas no plano. Quando desligado, o plano fica como referência visual mas o pick lê o ponto real do modelo."
            >
              <span>Bloquear ao plano</span>
              <input
                type="checkbox"
                checked={snap}
                onChange={(e) => setSnap(e.target.checked)}
                className="accent-[var(--color-accent)]"
              />
            </label>
          )}
          {workPlane && (
            <label
              className="flex w-full cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              title="Esconde a representação visual do plano (rectângulo translúcido + grelha) sem o desactivar. Útil quando queres usar o plano como referência mas sem o overlay laranja."
            >
              <span>Mostrar plano</span>
              <input
                type="checkbox"
                checked={planeVisible}
                onChange={(e) => setPlaneVisible(e.target.checked)}
                className="accent-[var(--color-accent)]"
              />
            </label>
          )}
          {workPlane && (
            <button
              type="button"
              onClick={() => {
                setWorkPlane(null)
                setOpen(false)
              }}
              className="mt-1 flex w-full items-center gap-2 rounded border-t border-[var(--color-border)]/60 px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
            >
              <Eraser size={12} />
              <span>Remover plano</span>
            </button>
          )}
          {workPlane && (
            <div className="mt-1 px-2 pb-1 pt-1 font-mono text-[9px] text-[var(--color-text-dim)]">
              Origem [{workPlane.origin.map((v) => v.toFixed(2)).join(', ')}]
            </div>
          )}
        </RibbonMenu>
      )}
    </div>
  )
}

interface UnitsControlProps {
  units: LengthUnit
  onChange: (u: LengthUnit) => void
}

function UnitsControl({ units, onChange }: UnitsControlProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  const labels: Record<LengthUnit, string> = {
    mm: 'mm',
    cm: 'cm',
    m: 'm',
    km: 'km',
  }

  return (
    <div
      ref={wrapRef}
      className="relative flex w-[68px] flex-col items-center px-1 pt-1"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`Unidades das medições (atual: ${labels[units]})`}
        className="flex w-full flex-col items-center gap-1 rounded px-1 py-1 text-center text-[10px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
      >
        <span className="font-mono text-[15px] leading-tight">
          {labels[units]}
        </span>
        <span className="leading-tight">Unidades</span>
      </button>
      {open && (
        <RibbonMenu
          open={open}
          anchorRef={wrapRef}
          onClose={() => setOpen(false)}
          width={120}
          align="center"
          ariaLabel="Unidades das medições"
        >
          {(['mm', 'cm', 'm', 'km'] as const).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => {
                onChange(u)
                setOpen(false)
              }}
              className={`flex w-full items-center justify-between rounded px-2 py-1 text-[11px] ${
                u === units
                  ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                  : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
              }`}
            >
              <span>{labels[u]}</span>
              <span className="font-mono text-[10px] text-[var(--color-text-dim)]">
                {u === 'mm' ? '0.001 m' : u === 'cm' ? '0.01 m' : u === 'km' ? '1000 m' : '1 m'}
              </span>
            </button>
          ))}
        </RibbonMenu>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Generic ribbon UI primitives
// ---------------------------------------------------------------------------

interface TabButtonProps {
  label: string
  active: boolean
  onClick: () => void
}

function TabButton({ label, active, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-colors ${
        active
          ? 'text-[var(--color-accent)]'
          : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
      }`}
    >
      {label}
      {active && (
        <span
          aria-hidden
          className="absolute inset-x-1 -bottom-px h-[2px] bg-[var(--color-accent)]"
        />
      )}
    </button>
  )
}

interface RibbonPanelProps {
  title: string
  children: ReactNode
}

function RibbonPanel({ title, children }: RibbonPanelProps) {
  return (
    <div className="flex shrink-0 flex-col border-r border-[var(--color-border-subtle)] last:border-r-0">
      <div className="flex flex-1 items-start gap-1 px-2 pb-1 pt-2">
        {children}
      </div>
      <div className="px-2 pb-1 text-center text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
        {title}
      </div>
    </div>
  )
}

interface RibbonButtonProps {
  /** Marca o botão como alvo do guia de dois minutos. */
  dataTour?: string
  icon: LucideIcon
  label: string
  onClick: () => void
  disabled?: boolean
  accent?: boolean
  tooltip?: string
  badge?: string | null
}

function RibbonButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  accent,
  tooltip,
  badge,
  dataTour,
}: RibbonButtonProps) {
  return (
    <button
      type="button"
      data-tour={dataTour}
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      className={`relative flex w-[68px] flex-col items-center gap-1 rounded px-1 py-1 text-center text-[10px] transition-colors ${
        disabled
          ? 'cursor-not-allowed text-[var(--color-text-dim)] opacity-50'
          : accent
            ? 'text-[var(--color-accent)] hover:bg-[var(--color-accent-bg)]'
            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
      }`}
    >
      <Icon size={22} strokeWidth={1.5} />
      <span className="leading-tight">{label}</span>
      {badge && (
        <span className="absolute right-0 top-0 rounded bg-[var(--color-accent)] px-1 py-0 font-mono text-[9px] font-bold text-[var(--color-bg)]">
          {badge}
        </span>
      )}
    </button>
  )
}

interface RibbonToggleProps extends Omit<RibbonButtonProps, 'accent'> {
  pressed: boolean
  /** Render a "WIP" diagonal watermark stripe over the button.
   * Used for features that are functional but the result is known
   * to have visual / accuracy issues, keeps them discoverable
   * without misleading the user about quality. */
  wip?: boolean
}

/** Um painel que também tem ícone na barra lateral: o mesmo estado e as
 *  mesmas funções (rail/painelDaBarra.ts), para o painel aparecer aberto
 *  nos dois sítios. Pedido do Rui (18/10/2026): "tudo o que está na
 *  barra lateral tem de ter espaço no ribbon". */
function RibbonPainelDaBarra({ id }: { id: RailItemId }) {
  const aberto = useAppStore((s) => painelAberto(s, id) === true)
  const item = RAIL_ITEMS.find((i) => i.id === id)
  if (!item) return null
  return (
    <RibbonToggle
      icon={ICONES_DA_BARRA[id]}
      label={item.label}
      pressed={aberto}
      onClick={() => alternarPainel(useAppStore.getState(), id)}
      tooltip={`${item.descricao}. Também na barra lateral.`}
    />
  )
}

function RibbonToggle({
  icon: Icon,
  label,
  onClick,
  disabled,
  tooltip,
  pressed,
  badge,
  wip,
}: RibbonToggleProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      disabled={disabled}
      title={
        (wip ? '⚠ Em desenvolvimento: pode estar visualmente impreciso. ' : '') +
        (tooltip ?? '')
      }
      className={`relative flex w-[68px] flex-col items-center gap-1 overflow-hidden rounded px-1 py-1 text-center text-[10px] transition-colors ${
        disabled
          ? 'cursor-not-allowed text-[var(--color-text-dim)] opacity-50'
          : pressed
            ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent-border)]'
            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
      }`}
    >
      <Icon size={22} strokeWidth={1.5} />
      <span className="leading-tight">{label}</span>
      {badge && (
        <span className="absolute right-0 top-0 rounded bg-[var(--color-accent)] px-1 py-0 font-mono text-[9px] font-bold text-[var(--color-bg)]">
          {badge}
        </span>
      )}
      {wip && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <span className="rotate-[-18deg] rounded border border-[var(--color-warn-border)] bg-[var(--color-accent-bg)] px-1 py-0 text-[8px] font-bold tracking-wider text-[var(--color-accent)]">
            WIP
          </span>
        </span>
      )}
    </button>
  )
}

interface RibbonSmallToggleProps {
  icon: LucideIcon
  label: string
  onClick: () => void
  pressed: boolean
  tooltip?: string
}

function RibbonSmallToggle({
  icon: Icon,
  label,
  onClick,
  pressed,
  tooltip,
}: RibbonSmallToggleProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      title={tooltip}
      className={`flex items-center gap-1.5 self-start rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
        pressed
          ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
          : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
      }`}
    >
      <Icon size={12} strokeWidth={1.6} />
      <span>{label}</span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// LoadingPill: surfaces the current IFC loading stage in the tab strip.
// ---------------------------------------------------------------------------
//
// The viewer's useViewer hook owns the loading/stage React state, but
// since the ribbon lives outside the canvas tree we rebroadcast it via
// a tiny window-level event. useViewer dispatches 'vn2r:viewer-stage'
// with { detail: { loading, stage } } whenever it changes.

// Small adjacent control for the Ghost toggle: shows the current
// opacity as a percentage and opens a popover with a range slider
// so the user can tune transparency without opening the floating
// Aparência panel. Hidden when Ghost is off.
function GhostOpacityButton({
  enabled,
  opacity,
  estilo,
}: {
  enabled: boolean
  opacity: number
  estilo: 'contornos' | 'translucido'
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const pct = Math.round(opacity * 100)
  if (!enabled) return null
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Estilo do ghost: contornos (o resto do modelo só com as arestas, sem tapar a selecção) ou translúcido."
        className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-accent)]"
      >
        {estilo === 'contornos' ? 'Contornos' : `${pct}%`} ▾
      </button>
      {open && (
        <RibbonMenu
          open={open}
          anchorRef={wrapRef}
          onClose={() => setOpen(false)}
          width={224}
          ariaLabel="Estilo do fantasma"
        >
          <div className="mb-2 flex gap-1 text-[10px]" role="radiogroup" aria-label="Estilo do ghost">
            {(
              [
                ['contornos', 'Contornos', 'O resto do modelo fica só com as arestas, esbatidas: vê-se tudo sem tapar a selecção.'],
                ['translucido', 'Translúcido', 'O resto do modelo fica com uma película cinzenta por cima.'],
              ] as const
            ).map(([valor, rotulo, dica]) => (
              <button
                key={valor}
                type="button"
                role="radio"
                aria-checked={estilo === valor}
                title={dica}
                onClick={() => void viewerApi.setGhostEstilo(valor)}
                className={`flex-1 rounded border px-1.5 py-0.5 ${
                  estilo === valor
                    ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                    : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                {rotulo}
              </button>
            ))}
          </div>
          {estilo === 'contornos' && (
            <div className="text-[10px] leading-snug text-[var(--color-text-muted)]">
              As faces do resto do modelo escondem-se e as arestas ficam esbatidas. Precisa das arestas ligadas (Aparência &gt; Arestas); sem elas, fica translúcido.
            </div>
          )}
          {estilo === 'translucido' && (
          <>
          <div className="mb-1 flex items-center justify-between text-[10px] text-[var(--color-text-muted)]">
            <span>Opacidade do ghost</span>
            <span className="font-mono text-[var(--color-text)]">{pct}%</span>
          </div>
          <input
            type="range"
            min={2}
            max={50}
            value={pct}
            onChange={(e) =>
              void viewerApi.setGhostOpacity(Number(e.target.value) / 100)
            }
            className="w-full"
          />
          <div className="mt-1 flex justify-between font-mono text-[9px] text-[var(--color-text-dim)]">
            <span>2%</span>
            <span>50%</span>
          </div>
          </>
          )}
        </RibbonMenu>
      )}
    </div>
  )
}

function LoadingPill() {
  const [stage, setStage] = useState<{
    loading: boolean
    stage: string | null
  }>({ loading: false, stage: null })

  useEffect(() => {
    const onStage = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { loading: boolean; stage: string | null }
        | undefined
      if (detail) setStage(detail)
    }
    window.addEventListener('vn2r:viewer-stage', onStage as EventListener)
    return () =>
      window.removeEventListener(
        'vn2r:viewer-stage',
        onStage as EventListener,
      )
  }, [])

  if (!stage.loading || !stage.stage) return null
  return (
    <span className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)]">
      {stage.stage}
    </span>
  )
}

// ---------------------------------------------------------------------------
// ShortcutsHelp: modal listing every shortcut from the central
// registry, with per-action rebinding, reset, and JSON import/export
// for sharing between machines or co-workers.
// ---------------------------------------------------------------------------

function ShortcutsHelp() {
  const [open, setOpen] = useState(false)
  // Local state used to rerender when bindings change.
  const [, force] = useState(0)
  useEffect(() => subscribeBindings(() => force((n) => n + 1)), [])
  // The "?" key opens/closes, registered through the central
  // dispatcher, not a window listener, so the user's rebind takes
  // effect.
  useShortcutHandler('ribbon.openHelp', () => setOpen((v) => !v))
  useEscape(open, () => setOpen(false))

  const groups = useMemo(() => {
    const map = new Map<string, typeof ACTIONS>()
    for (const a of ACTIONS) {
      const arr = map.get(a.group) ?? []
      arr.push(a)
      map.set(a.group, arr)
    }
    return Array.from(map.entries())
  }, [])

  const onExport = () => {
    const text = exportBindings()
    const blob = new Blob([text], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const stamp = new Date().toISOString().slice(0, 10)
    a.href = url
    a.download = `vn2r-shortcuts-${stamp}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const fileRef = useRef<HTMLInputElement | null>(null)
  const onPickImport = () => fileRef.current?.click()
  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      const result = importBindings(text)
      const skipped = result.skipped.length
        ? `\n(${result.skipped.length} desconhecidos ignorados: ${result.skipped.slice(0, 5).join(', ')}${result.skipped.length > 5 ? '…' : ''})`
        : ''
      alert(`Atalhos importados: ${result.applied}.${skipped}`)
    } catch (err) {
      alert(
        `Falha a importar: ${err instanceof Error ? err.message : String(err)}`,
      )
    }
    e.target.value = ''
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Atalhos de teclado (?)"
        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
      >
        <Keyboard size={14} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Atalhos de teclado"
          // Banda dos diálogos (CAMADA.dialogos), ver core/layout/camadas.ts.
          className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/40 pt-16"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] w-[640px] overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-2xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--color-text)]">
                Atalhos de teclado
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onPickImport}
                  title="Importar ficheiro de atalhos (JSON)"
                  className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  Importar
                </button>
                <button
                  type="button"
                  onClick={onExport}
                  title="Exportar atalhos atuais para um ficheiro JSON"
                  className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  Exportar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      confirm('Repor todos os atalhos para os valores por defeito?')
                    ) {
                      resetAllBindings()
                    }
                  }}
                  title="Repor todos os atalhos para os valores de fábrica"
                  className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  Repor
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded px-2 py-0.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="mb-3 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 px-2 py-1.5 text-[11px] text-[var(--color-text-muted)]">
              Click no atalho para redefinir. Esc cancela. Para ações de
              ratoeira: <strong>LMB drag em vazio</strong> = janela de
              seleção (azul = contido, verde = intersecta).{' '}
              <strong>RMB drag</strong> = orbit. <strong>MMB drag</strong> =
              pan. <strong>Wheel</strong> = zoom.{' '}
              <strong>Double-click num elemento</strong> = pivot sem mover a
              câmara.
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={onImport}
            />
            <div className="space-y-3">
              {groups.map(([group, actions]) => (
                <div key={group}>
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-[var(--color-accent)]">
                    {group}
                  </div>
                  <table className="w-full table-fixed border-collapse text-xs">
                    <tbody>
                      {actions.map((a) => (
                        <ShortcutRow key={a.id} actionId={a.id} label={a.label} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function ShortcutRow({
  actionId,
  label,
}: {
  actionId: string
  label: string
}) {
  const [listening, setListening] = useState(false)
  const cancelRef = useRef<(() => void) | null>(null)
  const chord = getChord(actionId)
  const startListening = () => {
    if (listening) return
    setListening(true)
    cancelRef.current = captureNextChord((result) => {
      cancelRef.current = null
      setListening(false)
      if (result === 'cancel') return
      setChord(actionId, result)
    })
  }
  useEffect(() => () => cancelRef.current?.(), [])
  return (
    <tr className="border-b border-[var(--color-border)]/40 last:border-b-0">
      <td className="py-1 pr-2 align-top text-[var(--color-text-muted)]">
        {label}
      </td>
      <td className="w-[180px] py-1 pl-2 align-top text-right">
        <button
          type="button"
          onClick={startListening}
          className={`rounded border px-2 py-0.5 font-mono text-[10px] ${
            listening
              ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
              : chord
                ? 'border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-accent)]'
                : 'border-dashed border-[var(--color-border)] text-[var(--color-text-dim)] hover:border-[var(--color-accent)] hover:text-[var(--color-text-muted)]'
          }`}
          title={
            listening
              ? 'A ouvir... carrega na nova combinação. Esc cancela.'
              : 'Click para redefinir o atalho'
          }
        >
          {listening ? 'a ouvir...' : (chord ?? '-')}
        </button>
        <button
          type="button"
          onClick={() => resetChord(actionId)}
          title="Repor para o valor por defeito"
          className="ml-1 rounded px-1 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        >
          ↺
        </button>
      </td>
    </tr>
  )
}

export default Ribbon
